import type { ParticipantEditFieldState, ParticipantEditPhoneNumbersFieldState } from '../../selectors'
import { Typography } from '@sunwebgroup/uds-ui/typography'
import { useIntl } from '@/intl'
import { PhoneInput } from '@/scopes/common/components/FormElements/PhoneInput'
import { TextInput } from '@/scopes/common/components/FormElements/TextInput'
import { getPhoneDigitsFromPattern, getPhoneExampleFromDigits } from '@/scopes/common/components/FormElements/utils/phonePatternUtils'
import { fieldProps } from '../../utils/participantEdit/participantEditFields'
import styles from './ParticipantEditShared.module.css'

interface ParticipantEditContactSectionProps {
  fields: {
    email: ParticipantEditFieldState | undefined
    mobilePhoneNumbers: ParticipantEditPhoneNumbersFieldState | undefined
    emergencyPhoneNumbers: ParticipantEditPhoneNumbersFieldState | undefined
  }
}

interface PhoneNumberInputsProps {
  name: string
  field: ParticipantEditPhoneNumbersFieldState
  labelKey: string
  patternErrorKey: string
  requiredErrorKey: string
  hintKey?: string
}

export function ParticipantEditContactSection({ fields }: ParticipantEditContactSectionProps) {
  const { formatMessage } = useIntl()
  const { email, mobilePhoneNumbers, emergencyPhoneNumbers } = fields

  if (!email && !mobilePhoneNumbers && !emergencyPhoneNumbers) {
    return null
  }

  return (
    <div className={styles.section}>
      <Typography as="h2" variant="display-h-regular">
        {formatMessage({ id: 'booking_details_participant_edit_contact_section_title' })}
      </Typography>
      <div className={styles.fieldsStack}>
        {email && (
          <TextInput
            name="email"
            label={formatMessage({ id: 'email_address' })}
            {...fieldProps(email, formatMessage)}
            pattern={email.pattern}
            patternErrorMessage={formatMessage({ id: 'booking_details_participant_edit_email_pattern_error' })}
            requiredErrorMessage={formatMessage({ id: 'booking_details_participant_edit_email_required_error' })}
          />
        )}
        {mobilePhoneNumbers && (
          <PhoneNumberInputs
            name="mobilePhoneNumbers"
            field={mobilePhoneNumbers}
            labelKey="booking_details_participant_edit_mobile_phone_label"
            hintKey="booking_details_participant_edit_mobile_phone_help_text"
            patternErrorKey="booking_details_participant_edit_mobile_phone_pattern_error"
            requiredErrorKey="booking_details_participant_edit_mobile_phone_required_error"
          />
        )}
        {emergencyPhoneNumbers && (
          <PhoneNumberInputs
            name="emergencyPhoneNumbers"
            field={emergencyPhoneNumbers}
            labelKey="booking_details_participant_edit_emergency_phone_label"
            patternErrorKey="booking_details_participant_edit_emergency_phone_pattern_error"
            requiredErrorKey="booking_details_participant_edit_emergency_phone_required_error"
          />
        )}
      </div>
    </div>
  )
}

function PhoneNumberInputs({ name, field, labelKey, patternErrorKey, requiredErrorKey, hintKey }: PhoneNumberInputsProps) {
  const { formatMessage } = useIntl()

  return (
    <>
      {field.values.map((_, index) => (
        <PhoneInput
          // The inputs are positional and the list never reorders, so the index identifies them.
          // eslint-disable-next-line react/no-array-index-key
          key={index}
          name={`${name}.${index}`}
          label={formatMessage({ id: labelKey })}
          countries={field.countries}
          patternErrorMessage={selectedCountry => formatMessage(
            { id: patternErrorKey },
            {
              phoneDigits: getPhoneDigitsFromPattern(selectedCountry?.pattern),
              phoneExample: getPhoneExampleFromDigits(getPhoneDigitsFromPattern(selectedCountry?.pattern)),
            },
          )}
          {...fieldProps(field, formatMessage, hintKey)}
          requiredErrorMessage={formatMessage({ id: requiredErrorKey })}
          // Legacy marks only the first number of a type as mandatory.
          required={field.required && index === 0}
          modal
        />
      ))}
    </>
  )
}
